import React from 'react';

import { PersonAvailability } from '@/types/personAvailability';

import { SummaryOptions } from './types';
import { generateAvailabilityGroups } from './utils';

/**
 * Renders availability summary with clean markup
 */
export function AvailabilitySummary({
  availability,
  options
}: {
  availability: PersonAvailability | null;
  options?: SummaryOptions;
}): React.ReactElement {
  const groups = generateAvailabilityGroups(availability, options);

  if (groups.length === 0) {
    return <span className="text-gray-500">No regular schedule set</span>;
  }

  // Separate available and unavailable groups
  const availableGroups = groups.filter((g) => g.type === 'available');
  const unavailableGroups = groups.filter((g) => g.type === 'unavailable');

  // Format last updated timestamp
  const lastUpdated = availability?.updated_at
    ? new Date(availability.updated_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    : null;

  return (
    <div className="space-y-2">
      {availableGroups.length > 0 && (
        <div>
          <ul className="list-none space-y-1 ml-0">
            {availableGroups.map((group, idx) => (
              <li key={`available-${idx}`} className="text-sm">
                <span className="font-medium">{group.label}:</span>{' '}
                <span className="text-gray-700">{group.intervals}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {unavailableGroups.length > 0 && (
        <div>
          <div className="font-semibold text-sm text-gray-700 mb-1">Unavailable</div>
          <ul className="list-none space-y-1 ml-0">
            {unavailableGroups.map((group, idx) => (
              <li key={`unavailable-${idx}`} className="text-sm text-gray-700">
                {group.label}
                {group.dateRange && group.dayCount && (
                  <span className="text-gray-500 ml-1">({group.dayCount} days)</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {lastUpdated && (
        <div className="pt-1 mt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            <span className="font-medium">Last updated:</span> {lastUpdated}
          </p>
        </div>
      )}
    </div>
  );
}
